/**
 * Theme Service
 * Provides theme-related utilities like style objects based on theme mode.
 * Dark-mode values follow the "Clinical Calm" token set defined in index.css
 * (deep ink surfaces + single azure accent). Keep these in sync with the
 * CSS custom properties there.
 */

/**
 * Get theme styles object based on dark mode state
 * @param {boolean} isDarkMode Whether dark mode is active
 * @returns {Object} Theme style object with color values
 */
export const getThemeStyles = (isDarkMode) => {
  return {
    container: {
      background: isDarkMode ? '#0e1626' : '#f5f7fa'
    },
    content: {
      background: isDarkMode ? '#151e30' : 'rgba(255, 255, 255, 0.9)',
      shadow: isDarkMode ? '0 1px 2px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.28)' : '0 4px 10px rgba(0, 0, 0, 0.1)'
    },
    text: {
      primary: isDarkMode ? '#eaf0f7' : '#2c3e50',
      secondary: isDarkMode ? '#9fb0c5' : '#6c757d'
    },
    background: {
      primary: isDarkMode ? '#0e1626' : '#f5f7fa',
      secondary: isDarkMode ? '#1c2740' : '#e9ecef'
    },
    header: {
      background: isDarkMode ? 'rgba(14, 22, 38, 0.92)' : '#f8f9fa'
    },
    input: {
      background: isDarkMode ? '#1c2740' : '#ffffff',
      color: isDarkMode ? '#eaf0f7' : '#2c3e50',
      border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.16)' : '1px solid #dcdfe6'
    },
    select: {
      background: isDarkMode ? '#1c2740' : 'rgba(255, 255, 255, 0.9)',
      color: isDarkMode ? '#eaf0f7' : '#2c3e50',
      border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.16)' : '1px solid #dcdfe6',
      borderRadius: '5px',
      padding: '8px 12px'
    },
    button: {
      primary: {
        background: isDarkMode ? '#3d8bff' : '#007bff',
        color: '#ffffff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
      },
      secondary: {
        background: isDarkMode ? '#1c2740' : '#e9ecef',
        color: isDarkMode ? '#eaf0f7' : '#495057',
        border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.16)' : '1px solid #dcdfe6',
        borderRadius: '5px',
        cursor: 'pointer'
      },
      danger: {
        background: isDarkMode ? '#f87171' : '#dc3545',
        color: '#ffffff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
      }
    },
    progressBar: {
      background: isDarkMode ? 'rgba(148, 163, 184, 0.16)' : '#e9ecef',
      fill: isDarkMode ? '#3d8bff' : '#007bff'
    },
    answerOption: {
      background: isDarkMode ? '#1c2740' : '#f1f3f5',
      color: isDarkMode ? '#eaf0f7' : '#2c3e50',
      border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.16)' : '1px solid #dcdfe6'
    },
    explanationSection: {
      background: isDarkMode ? '#1c2740' : '#f1f3f5',
      color: isDarkMode ? '#eaf0f7' : '#2c3e50',
      border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.16)' : '1px solid #dcdfe6'
    },
    error: {
      color: isDarkMode ? '#f87171' : '#dc3545'
    },
    boxShadow: isDarkMode ? '0 1px 2px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.28)' : '0 2px 4px rgba(0,0,0,0.1)'
  };
};

/**
 * Get CSS variables based on current theme
 * @param {boolean} isDarkMode Whether dark mode is active
 * @returns {Object} CSS variables as an object
 */
export const getThemeVariables = (isDarkMode) => {
  return {
    '--primary-bg': isDarkMode ? '#0e1626' : '#f5f7fa',
    '--secondary-bg': isDarkMode ? '#1c2740' : '#e9ecef',
    '--primary-text': isDarkMode ? '#eaf0f7' : '#2c3e50',
    '--secondary-text': isDarkMode ? '#9fb0c5' : '#6c757d',
    '--primary-accent': isDarkMode ? '#3d8bff' : '#007bff'
  };
};
